import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, MapPin, Users, Home, DollarSign } from 'lucide-react';
import AutocompleteInput from '@/components/AutocompleteInput';
import GeolocationButton from '@/components/GeolocationButton';

export const HeroSearch = ({ onSearch }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [location, setLocation] = useState('');
  const [guests, setGuests] = useState('2');
  const [bedrooms, setBedrooms] = useState('any');
  const [maxPrice, setMaxPrice] = useState('any');

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (location) params.append('location', location);
    if (guests) params.append('guests', guests);
    if (bedrooms !== 'any') params.append('bedrooms', bedrooms);
    if (maxPrice !== 'any') params.append('max_price', maxPrice);
    
    if (onSearch) {
      onSearch({ location, guests, bedrooms, maxPrice });
    } else {
      navigate(`/search?${params.toString()}`);
    }
  };

  const handlePropertySelect = (property) => {
    // Navigate directly to property page if property is selected
    navigate(`/properties/${property.id}`);
  };

  const handleNearMe = (coords) => {
    // Search properties near the user's location
    setLocation(`${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`);
    const params = new URLSearchParams();
    params.append('lat', coords.latitude);
    params.append('lng', coords.longitude);
    params.append('radius', '10'); // 10 miles radius
    if (guests) params.append('guests', guests);
    navigate(`/search?${params.toString()}`);
  };

  const isPtBR = i18n.language === 'pt-BR' || i18n.language === 'pt';

  // Translations for guest labels
  const guestLabel = (num) => {
    if (!isPtBR) {
      return `${num} ${num === 1 ? 'guest' : 'guests'}`;
    }
    return `${num} ${num === 1 ? 'hóspede' : 'hóspedes'}`;
  };

  // Translations for bedroom labels
  const bedroomLabel = (num) => {
    if (!isPtBR) {
      return `${num}+ ${num === 1 ? 'bedroom' : 'bedrooms'}`;
    }
    return `${num}+ ${num === 1 ? 'quarto' : 'quartos'}`;
  };

  return (
    <div
      className="w-full max-w-5xl lg:max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6"
      role="search"
      data-testid="hero-search"
    >
      <div className="rounded-2xl p-3 md:p-4 flex flex-col lg:flex-row flex-wrap gap-3 items-stretch lg:items-center border glass shadow-lg">
        {/* Location with Autocomplete */}
        <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent/50 transition-colors relative z-50">
          <MapPin className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          <AutocompleteInput
            value={location}
            onChange={setLocation}
            onSelect={handlePropertySelect}
            placeholder={t('home.locationPlaceholder')}
            className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 p-0"
          />
        </div>

        <div className="hidden lg:block w-px h-8 bg-border flex-shrink-0" />

        {/* Guests */}
        <div className="flex-1 lg:flex-initial lg:w-36 xl:w-40 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent/50 transition-colors min-w-0">
          <Users className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          <Select value={guests} onValueChange={setGuests}>
            <SelectTrigger
              className="border-0 bg-transparent focus:ring-0 focus:ring-offset-0 p-0 h-auto text-sm"
              data-testid="hero-search-guests"
            >
              <SelectValue placeholder={t('common.guests')} />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map((num) => (
                <SelectItem key={num} value={String(num)}>
                  {guestLabel(num)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="hidden lg:block w-px h-8 bg-border flex-shrink-0" />

        {/* Bedrooms */}
        <div className="flex-1 lg:flex-initial lg:w-36 xl:w-40 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent/50 transition-colors min-w-0">
          <Home className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          <Select value={bedrooms} onValueChange={setBedrooms}>
            <SelectTrigger
              className="border-0 bg-transparent focus:ring-0 focus:ring-offset-0 p-0 h-auto text-sm"
              data-testid="hero-search-bedrooms"
            >
              <SelectValue placeholder={t('common.bedrooms')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{isPtBR ? 'Qualquer' : 'Any'}</SelectItem>
              <SelectItem value="1">{bedroomLabel(1)}</SelectItem>
              <SelectItem value="2">{bedroomLabel(2)}</SelectItem>
              <SelectItem value="3">{bedroomLabel(3)}</SelectItem>
              <SelectItem value="4">{bedroomLabel(4)}</SelectItem>
              <SelectItem value="5">{bedroomLabel(5)}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="hidden lg:block w-px h-8 bg-border flex-shrink-0" />

        {/* Max Price */}
        <div className="flex-1 lg:flex-initial lg:w-36 xl:w-40 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent/50 transition-colors min-w-0">
          <DollarSign className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          <Select value={maxPrice} onValueChange={setMaxPrice}>
            <SelectTrigger
              className="border-0 bg-transparent focus:ring-0 focus:ring-offset-0 p-0 h-auto text-sm"
              data-testid="hero-search-price"
            >
              <SelectValue placeholder={isPtBR ? 'Preço' : 'Price'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t('home.anyPrice')}</SelectItem>
              <SelectItem value="100">{isPtBR ? 'Até $100' : 'Up to $100'}</SelectItem>
              <SelectItem value="150">{isPtBR ? 'Até $150' : 'Up to $150'}</SelectItem>
              <SelectItem value="200">{isPtBR ? 'Até $200' : 'Up to $200'}</SelectItem>
              <SelectItem value="300">{isPtBR ? 'Até $300' : 'Up to $300'}</SelectItem>
              <SelectItem value="500">{isPtBR ? 'Até $500' : 'Up to $500'}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Near Me Button (Mobile/Desktop) */}
        <GeolocationButton
          onLocationFound={handleNearMe}
          variant="outline"
          size="lg"
          className="hidden sm:flex"
        />

        {/* Search Button */}
        <Button
          onClick={handleSearch}
          className="bg-primary text-white hover:bg-[#0B2246] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md lg:px-8 flex-shrink-0"
          size="lg"
          data-testid="hero-search-button"
        >
          <Search className="w-5 h-5 lg:mr-2" />
          <span className="hidden lg:inline">{t('common.search')}</span>
        </Button>
      </div>
      
      {/* Mobile Near Me Button */}
      <div className="mt-2 sm:hidden">
        <GeolocationButton
          onLocationFound={handleNearMe}
          variant="outline"
          size="sm"
          className="w-full"
        />
      </div>
    </div>
  );
};
